// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import { createEMRHub } from "../../api";

export const useCreateEMRHub = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (payload: { alias: string }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return createEMRHub(token, clinicId, payload);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emr-hubs", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("EMR hub created successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("EMR hub could not be created!"),
          t
        ),
      });
    },
  });
};
